/**
 * Cookie utility functions for managing cookie consent
 */

const COOKIE_CONSENT_KEY = 'dailyvents_cookie_consent'
const COOKIE_EXPIRY_DAYS = 365

export interface CookieConsent {
    necessary: boolean
    statistics: boolean
    marketing: boolean
    timestamp: number
}

/**
 * Get cookie value by name
 */
const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
        return parts.pop()?.split(';').shift() || null
    }
    return null
}

/**
 * Set cookie with expiration
 */
const setCookie = (
    name: string,
    value: string,
    days: number = COOKIE_EXPIRY_DAYS
): void => {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    const expires = `expires=${date.toUTCString()}`
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`
}

/**
 * Check if cookie consent has been given
 */
export const hasCookieConsent = (): boolean => {
    const consent = getCookie(COOKIE_CONSENT_KEY)
    return consent !== null
}

/**
 * Get cookie consent preferences
 */
export const getCookieConsent = (): CookieConsent | null => {
    const consent = getCookie(COOKIE_CONSENT_KEY)
    if (!consent) {
        return null
    }

    try {
        return JSON.parse(decodeURIComponent(consent)) as CookieConsent
    } catch {
        return null
    }
}

/**
 * Save cookie consent preferences
 */
export const saveCookieConsent = (consent: {
    necessary: boolean
    statistics: boolean
    marketing: boolean
}): void => {
    const consentData: CookieConsent = {
        ...consent,
        timestamp: Date.now(),
    }

    const consentString = JSON.stringify(consentData)
    setCookie(COOKIE_CONSENT_KEY, encodeURIComponent(consentString))
}

/**
 * Clear cookie consent (for testing or reset)
 */
export const clearCookieConsent = (): void => {
    document.cookie = `${COOKIE_CONSENT_KEY}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`
}
